import { Combobox } from "@/components/ui/combobox"
import { Input as ShadcnInput } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Menubar, MenubarContent, MenubarItem, MenubarMenu, MenubarTrigger } from "@/components/ui/menubar"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Switch } from "@/components/ui/switch"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/textarea"
import { agents } from "@/lib/agents"
import { loadVersion, saveVersion } from "@/lib/agents/agentfactory"
import { supabase } from "@/lib/agents/db"
import { app } from "@/lib/app"
import { AGENT_SETTINGS_CHANGED_EVENT, DEFAULT_BUILTIN_FLAGS, MAX_FLAGS } from "@/lib/agents/escalationFlags"
import { createModel } from "@/lib/data/model"
import { usePermissions } from "@/lib/hooks/usePermissions"
import { useTeamStore } from "@/lib/hooks/useTeam"
import { useForceUpdate } from "@/lib/utils"
import { cloneDeep } from "lodash"
import { AlertTriangle, BrainCog, Bug, CircleX, Cog, Download, Flag, GitFork, GitMerge, HelpCircle, LoaderCircle, MessageCircle, Play, Plus, RotateCcw, Save, Settings, Trash2, User, Workflow, Wrench, Zap } from "lucide-react"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { UseFormWatch } from "react-hook-form"
import { toast } from "sonner"
import { Col, Input, InputTextArea, Modal, Row, Select, useForm } from "../forms"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../ui/alert-dialog"
import { Button } from "../ui/button"
import { Separator } from "../ui/separator"

interface Props {
  update?: () => void
  onShowChat?: () => void
  rebuild: () => void
}

function VersionSaver({ watch, field, team, rebuild, hide }: { field: Field, watch: UseFormWatch<any>, team: string, rebuild: () => void, hide: () => void }) {

  const [versionTitle, setVersionTitle] = useState("")
  const update = useForceUpdate()

  watch((value, { name }) => {
    if (name === "version") setVersionTitle(value.version)
  })

  function onSaveVersion() {
    console.log("Saving Version...")
    saveVersion(versionTitle, app.agent, team)
    setVersionTitle("")
  }

  function onRemoveVersion(i: number) {
    console.log("Removing Version...", i)
    app.agent.versions.splice(i, 1)
    update()
  }

  function onLoadVersion(i: number) {
    console.log("Loading Version...", i)
    const loaded = loadVersion(i, app.agent)
    app.agent = loaded
    rebuild()
    app.agent.update()
    hide()
  }

  return <>
    <div>
      <Row className="my-4">
        <Input span={8} field={field} />
        <Col span={4} className="pt-5">
          <Button disabled={!versionTitle} onClick={onSaveVersion}>Create Version</Button>
        </Col>
      </Row>
    </div>
    <ScrollArea className="h-64  rounded-md border w-full">
      <div className="p-4">
        {app.agent.versions.map((version, i) => (
          <div key={i} >
            <div className="flex">
              <div className=" grow p-2 text-sm " onClick={() => onLoadVersion(i)}>{version.title}</div>
              <div className="flex">
                <div title="Load Version">
                  <Download className="text-blue-500 mt-3 cursor-pointer ml-4" size={16} onClick={() => onLoadVersion(i)} />
                </div>
                <div title="Remove Version">
                  <CircleX className="text-red-500 mt-3 cursor-pointer ml-4" size={16} onClick={() => onRemoveVersion(i)} />
                </div>
              </div>
            </div>
            <Separator className="my-2" />
          </div>
        ))}
      </div>
    </ScrollArea>

  </>
}




const agentModel = createModel({
  fields: {
    title: { type: 'string', title: 'Title' },
    description: { type: 'string', title: 'Description' },
    type: { type: 'string', title: 'Type', list: [{ value: "conversational", label: "Conversational" }, { value: "data", label: "Data" }] },
    contact: { type: 'string', title: 'Contact', },
    debuguid: { type: 'string', title: 'Debug UID' },
    version: { type: 'string', title: 'Version Title' },
  }
})

// Module-level cache for eval_items
let evalItemsCache: { id: number; name: string }[] | null = null


function EvaluationTab({ selectedItems, onSelectedItemsChange, initialIds, initializedRef, summaryLanguage, onSummaryLanguageChange, team }: { selectedItems: { id: number; name: string }[], onSelectedItemsChange: (items: { id: number; name: string }[]) => void, initialIds?: number[], initializedRef: React.MutableRefObject<boolean>, summaryLanguage: string, onSummaryLanguageChange: (value: string) => void, team: string }) {
  const [evalItems, setEvalItems] = useState<{ id: number; name: string }[]>([])
  const [comboValue, setComboValue] = useState<any>("")
  const [localSummaryLanguage, setLocalSummaryLanguage] = useState(summaryLanguage)

  useEffect(() => {
    const load = (items: { id: number; name: string }[]) => {
      setEvalItems(items)
      if (!initializedRef.current) {
        initializedRef.current = true
        const preselected = items.filter((i) => (initialIds || []).includes(i.id))
        onSelectedItemsChange(preselected)
      }
    }
    if (evalItemsCache) { load(evalItemsCache); return }
    supabase.from("eval_items").select("id, name").eq("team", team).then(({ data }) => {
      if (data) { evalItemsCache = data; load(data) }
    })
  }, [])

  function onSelectItem(value: any) {
    setComboValue(value)
    if (!value) return
    const item = evalItems.find((i) => i.id === value)
    if (!item || selectedItems.some((s) => s.id === item.id)) return
    onSelectedItemsChange([...selectedItems, item])
    setComboValue("")
  }

  function onRemoveItem(id: number) {
    onSelectedItemsChange(selectedItems.filter((i) => i.id !== id))
  }

  const unselected = evalItems.filter((i) => !selectedItems.some((s) => s.id === i.id))
  const comboItems = [
    ...(unselected.length > 0 ? [{ value: "__add_all__", label: "<Add All>" }] : []),
    ...unselected.map((i) => ({ value: i.id, label: i.name }))
  ]

  function onSelectItemWithAddAll(value: any) {
    if (value === "__add_all__") {
      onSelectedItemsChange([...selectedItems, ...unselected])
      setComboValue("")
      return
    }
    onSelectItem(value)
  }

  return <div className="flex flex-col gap-4 mt-4">
    <Combobox items={comboItems} value={comboValue} onChange={onSelectItemWithAddAll} placeholder="Search evaluation items..." />
    <ScrollArea className="h-48 rounded-md border w-full">
      <div className="p-2">
        {selectedItems.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No items selected</p>}
        {selectedItems.map((item) => (
          <div key={item.id}>
            <div className="flex items-center justify-between py-2 px-2">
              <span className="text-sm">{item.name}</span>
              <CircleX className="text-red-500 cursor-pointer shrink-0 ml-4" size={16} onClick={() => onRemoveItem(item.id)} />
            </div>
            <Separator />
          </div>
        ))}
      </div>
    </ScrollArea>
    {selectedItems.length > 0 && <Button type="button" variant="outline" size="sm" className="w-full text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => onSelectedItemsChange([])}>Remove All</Button>}
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="summary-language" className="text-sm font-medium">Summary Language</Label>
      <ShadcnInput id="summary-language" value={localSummaryLanguage} onChange={(e) => { setLocalSummaryLanguage(e.target.value); onSummaryLanguageChange(e.target.value) }} placeholder="e.g. English, Spanish..." />
    </div>
  </div>
}

function FormsTab({ formsRef, team }: { formsRef: React.MutableRefObject<string[]>, team: string }) {
  const [forms, setForms] = useState<{ id: string; title: string }[]>([])
  // Stored as string[] (0 or 1 id) for worker/panel compatibility.
  const [selectedId, setSelectedId] = useState<string | null>(() => formsRef.current?.[0] ?? null)
  const [comboValue, setComboValue] = useState<any>("")

  useEffect(() => {
    supabase.from("forms").select("id, title").eq("team", team).then(({ data }) => {
      if (data) setForms(data.map((f) => ({ id: f.id, title: f.title || "(untitled)" })))
    })
  }, [team])

  function commit(id: string | null) {
    setSelectedId(id)
    formsRef.current = id ? [id] : []
    app.agent.forms = formsRef.current
  }

  function onSelect(value: any) {
    setComboValue("")
    if (!value) return
    commit(value)
  }

  const selected = selectedId ? (forms.find((f) => f.id === selectedId) || { id: selectedId, title: selectedId }) : null
  const comboItems = forms.filter((f) => f.id !== selectedId).map((f) => ({ value: f.id, label: f.title }))

  return <div className="flex flex-col gap-4 mt-4">
    <p className="text-sm text-muted-foreground">
      Bind one form to this agent. It's filled automatically each turn (Magic Form); fields need an AI fill instruction or keyword rule to be extracted.
    </p>
    <Combobox items={comboItems} value={comboValue} onChange={onSelect} placeholder={selected ? "Change form..." : "Select a form..."} />
    <ScrollArea className="h-48 rounded-md border w-full">
      <div className="p-2">
        {!selected && <p className="text-sm text-muted-foreground text-center py-4">No form bound</p>}
        {selected && (
          <div className="flex items-center justify-between py-2 px-2">
            <span className="text-sm">{selected.title}</span>
            <CircleX className="text-red-500 cursor-pointer shrink-0 ml-4" size={16} onClick={() => commit(null)} />
          </div>
        )}
      </div>
    </ScrollArea>
  </div>
}

function IntegrationsTab() {
  const [whatsappToken, setWhatsappToken] = useState(() => app.agent.integrations?.whatsapp_token || "")
  const [whatsappPhoneId, setWhatsappPhoneId] = useState(() => app.agent.integrations?.whatsapp_phoneid || "")
  const [answerViaWhatsapp, setAnswerViaWhatsapp] = useState(() => app.agent.integrations?.telerivet_answerViaWhatsapp || false)

  return (
    <div className="flex flex-col gap-4 mt-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="whatsapp-token" className="text-sm font-medium">Whatsapp Token</Label>
        <ShadcnInput
          id="whatsapp-token"
          value={whatsappToken}
          onChange={e => setWhatsappToken(e.target.value)}
          onBlur={() => { app.agent.integrations = { ...app.agent.integrations, whatsapp_token: whatsappToken || null } }}
          placeholder="Enter Whatsapp Token..."
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="whatsapp-phoneid" className="text-sm font-medium">Whatsapp Phone id</Label>
        <ShadcnInput
          id="whatsapp-phoneid"
          value={whatsappPhoneId}
          onChange={e => setWhatsappPhoneId(e.target.value)}
          onBlur={() => { app.agent.integrations = { ...app.agent.integrations, whatsapp_phoneid: whatsappPhoneId || null } }}
          placeholder="Enter Whatsapp Phone id..."
        />
      </div>
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="telerivet-answer-via-whatsapp"
          checked={answerViaWhatsapp}
          onChange={e => {
            setAnswerViaWhatsapp(e.target.checked)
            app.agent.integrations = { ...app.agent.integrations, telerivet_answerViaWhatsapp: e.target.checked }
          }}
          className="h-4 w-4 cursor-pointer"
        />
        <Label htmlFor="telerivet-answer-via-whatsapp" className="text-sm font-medium cursor-pointer">Answer via Whatsapp</Label>
      </div>
    </div>
  )
}


const ICON_OPTIONS = [
  { value: "alert-triangle", label: "Alert", icon: AlertTriangle },
  { value: "help-circle", label: "Help", icon: HelpCircle },
  { value: "user", label: "User", icon: User },
  { value: "flag", label: "Flag", icon: Flag },
  { value: "zap", label: "Zap", icon: Zap },
  { value: "bug", label: "Bug", icon: Bug },
  { value: "message-circle", label: "Message", icon: MessageCircle },
  { value: "brain-cog", label: "Brain", icon: BrainCog },
  { value: "settings", label: "Settings", icon: Settings },
  { value: "cog", label: "Cog", icon: Cog },
  { value: "workflow", label: "Workflow", icon: Workflow },
  { value: "wrench", label: "Wrench", icon: Wrench },
]

function getIconComponent(iconName: string) {
  return ICON_OPTIONS.find(o => o.value === iconName)?.icon || Flag
}

function FlagRow({
  flag,
  onCommit,
  onRemove,
}: {
  flag: AgentEscalationFlag
  onCommit: (id: string, updates: Partial<AgentEscalationFlag>) => void
  onRemove: (id: string) => void
}) {
  const [localLabel, setLocalLabel] = useState(flag.label)
  const [localPrompt, setLocalPrompt] = useState(flag.detection_prompt)

  const IconComp = getIconComponent(flag.icon)

  return (
    <div className="border rounded-lg p-3 space-y-2.5">
      <div className="flex items-center gap-2">
        <IconComp className="h-4 w-4 text-muted-foreground shrink-0" />
        <ShadcnInput
          value={localLabel}
          onChange={e => setLocalLabel(e.target.value)}
          onBlur={() => onCommit(flag.id, { label: localLabel })}
          className="h-7 text-sm font-medium flex-1"
          placeholder="Flag name"
        />
        <div className="flex items-center gap-1.5 shrink-0">
          <span className="text-[10px] text-muted-foreground">{flag.enabled ? "On" : "Off"}</span>
          <Switch
            checked={flag.enabled}
            onCheckedChange={checked => onCommit(flag.id, { enabled: checked })}
            className="scale-75"
          />
        </div>
        {flag.type === "custom" && (
          <Button
            type="button" variant="ghost" size="icon"
            className="h-6 w-6 text-red-400 hover:text-red-600 shrink-0"
            onClick={() => onRemove(flag.id)}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      {flag.type === "custom" && (
        <div className="flex gap-1.5 flex-wrap">
          {ICON_OPTIONS.map(opt => {
            const IC = opt.icon
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onCommit(flag.id, { icon: opt.value })}
                className={`p-1.5 rounded border transition-colors ${flag.icon === opt.value ? "bg-primary/10 border-primary text-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}
                title={opt.label}
              >
                <IC className="h-3.5 w-3.5" />
              </button>
            )
          })}
        </div>
      )}

      <div>
        <Label className="text-[11px] text-muted-foreground mb-1 block">Detection Prompt</Label>
        <Textarea
          value={localPrompt}
          onChange={e => setLocalPrompt(e.target.value)}
          onBlur={() => onCommit(flag.id, { detection_prompt: localPrompt })}
          placeholder="Describe how to determine if this flag should be triggered..."
          className="text-xs min-h-[60px] resize-y"
          rows={3}
        />
      </div>

      {flag.type === "builtin" && (
        <p className="text-[10px] text-muted-foreground italic">Built-in flag - cannot be removed</p>
      )}
    </div>
  )
}

function EscalationFlagsTab({ flags, flagsRef }: { flags: AgentEscalationFlag[], flagsRef: React.MutableRefObject<AgentEscalationFlag[]> }) {
  const [localFlags, setLocalFlags] = useState<AgentEscalationFlag[]>(() => flags.length > 0 ? flags : DEFAULT_BUILTIN_FLAGS)

  // Write current flags into the ref so the parent can read them on save — no parent re-render triggered
  flagsRef.current = localFlags
  // And onto the agent, so the assistant reads and saves what's on screen
  app.agent.escalationFlags = localFlags

  const commitFlag = useCallback((id: string, updates: Partial<AgentEscalationFlag>) => {
    setLocalFlags(prev => prev.map(f => f.id === id ? { ...f, ...updates } : f))
  }, [])

  const removeFlag = useCallback((id: string) => {
    setLocalFlags(prev => prev.filter(f => f.id !== id))
  }, [])

  const addCustomFlag = useCallback(() => {
    setLocalFlags(prev => {
      if (prev.length >= MAX_FLAGS) return prev
      const newFlag: AgentEscalationFlag = {
        id: `custom_${Date.now()}`,
        type: "custom",
        label: "New Flag",
        icon: "flag",
        detection_prompt: "",
        enabled: true,
      }
      return [...prev, newFlag]
    })
  }, [])

  const customCount = localFlags.filter(f => f.type === "custom").length
  const builtinCount = localFlags.filter(f => f.type === "builtin").length

  return (
    <div className="flex flex-col gap-4 mt-4">
      <p className="text-xs text-muted-foreground">
        Configure escalation flags for this agent. The three built-in flags are always available. You can add up to {MAX_FLAGS - builtinCount} custom flags.
      </p>

      <div className="max-h-[min(42vh,360px)] min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain rounded-md border p-2">
        <div className="space-y-3 pr-1">
          {localFlags.map(flag => (
            <FlagRow
              key={flag.id}
              flag={flag}
              onCommit={commitFlag}
              onRemove={removeFlag}
            />
          ))}
        </div>
      </div>

      {customCount < (MAX_FLAGS - builtinCount) && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full gap-1.5"
          onClick={addCustomFlag}
        >
          <Plus className="h-3.5 w-3.5" />
          Add Custom Flag ({customCount}/{MAX_FLAGS - builtinCount})
        </Button>
      )}
    </div>
  )
}

export function Toolbar(props: Props) {
  const { selectedTeam } = useTeamStore()
  const update = useForceUpdate()
  const { canCreate, canUpdate } = usePermissions()

  const { form: agentForm, m: f, watch } = useForm(agentModel, {
    values: {
      title: app.agent.title,
    }
  })

  const [evalSelectedItems, setEvalSelectedItems] = useState<{ id: number; name: string }[]>([])
  const escalationFlagsRef = useRef<AgentEscalationFlag[]>(app.agent.escalationFlags || [])
  const formsRef = useRef<string[]>(app.agent.forms || [])
  const [activeTab, setActiveTab] = useState("settings")
  const evalInitialized = React.useRef(false)
  const [saving, setSaving] = useState(false)
  const [forking, setForking] = useState(false)
  const [merging, setMerging] = useState(false)
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false)
  const [mergeTarget, setMergeTarget] = useState<{ id: number; title: string } | null>(null)
  // Bumped when the assistant changes flags or forms, so those tabs reload from app.agent
  const [settingsVersion, setSettingsVersion] = useState(0)

  useEffect(() => {
    const reload = () => {
      escalationFlagsRef.current = app.agent.escalationFlags || []
      formsRef.current = app.agent.forms || []
      setSettingsVersion(v => v + 1)
    }
    window.addEventListener(AGENT_SETTINGS_CHANGED_EVENT, reload)
    return () => window.removeEventListener(AGENT_SETTINGS_CHANGED_EVENT, reload)
  }, [])


  agentForm.onSubmit = async (data) => {
    app.agent.title = data.title
    app.agent.description = data.description
    app.agent.type = data.type as any
    app.agent.debuguuid = data.debuguid || ""
  }

  async function onSave() {
    if (saving) return
    setSaving(true)

    const orgid = app.agent.id
    if (evalInitialized.current) {
      app.agent.evalItems = evalSelectedItems.map((item) => item.id)
    }
    const currentFlags = escalationFlagsRef.current
    app.agent.escalationFlags = currentFlags.length > 0 ? currentFlags : DEFAULT_BUILTIN_FLAGS
    app.agent.forms = formsRef.current

    const clonedAgent = cloneDeep(app.agent)
    const { data: { user } } = await supabase.auth.getUser()
    const saved = await agents.saveAgent(clonedAgent, selectedTeam.id, user?.id)
    app.agent.id = saved.id

    // A new agent only knows its team once it's loaded from the database
    if (!orgid) {
      window.location.assign("/agent/" + saved.id)
      return
    }

    toast("The flow was saved", {
      action: {
        label: "Ok",
        onClick: () => console.log("Ok"),
      },
    })
    setTimeout(() => {
      setSaving(false)
    }, 1000)
  }

  async function onFork() {
    if (forking) return
    if (!app.agent.id) {
      toast.error("Please save the agent first before forking")
      return
    }

    setForking(true)
    try {
      const clonedAgent = cloneDeep(app.agent)
      const forkedAgent = await agents.forkAgent(clonedAgent, selectedTeam.id)

      toast.success("Agent forked successfully", {
        description: `Created "${forkedAgent.title}"`,
      })

      window.location.href = "/agent/" + forkedAgent.id
    } catch (error) {
      toast.error("Failed to fork agent", {
        description: error instanceof Error ? error.message : "Unknown error",
      })
      setForking(false)
    }
  }

  async function onMerge() {
    if (merging) return
    if (!app.agent.fork_id) {
      toast.error("This agent is not a fork")
      return
    }

    setMerging(true)
    try {
      const clonedAgent = cloneDeep(app.agent)
      const result = await agents.mergeAgentIntoOriginal(clonedAgent, selectedTeam.id)

      if (result.hasConflicts) {
        const parts: string[] = []
        if (result.conflicts.workers.length > 0) parts.push(`${result.conflicts.workers.length} worker(s)`)
        if (result.conflicts.edges.length > 0) parts.push(`${result.conflicts.edges.length} connection(s)`)
        if (result.conflicts.fields.length > 0) parts.push(result.conflicts.fields.join(", "))
        toast.warning("Agent merged with conflicts", {
          description: `Both the original and this fork modified ${parts.join(", ")}. This fork's version was used for conflicting items. Check pre-merge snapshot in version history to review.`,
        })
      } else {
        toast.success("Agent merged successfully", {
          description: "Changes were merged into the original agent",
        })
      }

      window.location.href = "/agent/" + result.originalId
    } catch (error) {
      toast.error("Failed to merge agent", {
        description: error instanceof Error ? error.message : "Unknown error",
      })
      setMerging(false)
      setMergeDialogOpen(false)
    }
  }

  async function onPlay() {
    console.log("Play")

    const { agent } = app

    if (!agent.hasResponse()) {
      toast("Add a Response Worker to execute", { action: { label: "Ok", onClick: () => console.log("Ok"), }, })
      return
    }

    const apiKeys = await app.fetchAPIkeys(selectedTeam?.id)

    const p: AgentParameters = {
      debug: true,
      input: {},
      apiKeys: apiKeys,
    }

    await agent.execute(p)

    if (p.error) {
      toast("Error", {
        description: <div className="text-red-500 font-semibold">{p.error}</div>,
        action: {
          label: "Ok",
          onClick: () => console.log("Ok"),
        },
      })
    }

    console.log("Result: ", p.output)
  }

  function onChangeDisplayData() {
    app.agent.displayData = !app.agent.displayData
    app.agent.update()
    update()
  }

  function onSetAgent() {
    agentForm.edit({
      title: app.agent.title,
      description: app.agent.description,
      type: app.agent.type,
      debuguid: app.agent.debuguuid || "",
    })
  }

  async function onResetState() {
    if (!app.agent.debuguuid) return
    agentForm.modal.hide()
    await app.agent.resetAgent(app.agent.debuguuid)
  }

  async function onGenerateNewDebugUID() {
    const newUUID = crypto.randomUUID()
    if (app.agent.debuguuid) {
      await app.agent.resetAgent(app.agent.debuguuid)
      // await supabase.from("states").delete().eq("id", app.agent.debuguuid)
      // await supabase.from("history").delete().eq("uid", app.agent.debuguuid)
    }

    app.agent.debuguuid = newUUID
    agentForm.edit({
      title: app.agent.title,
      description: app.agent.description,
      type: app.agent.type,
      debuguid: newUUID
    })

    await onSave()

    toast("Success", {
      description: "New debug UID generated and agent saved",
      action: {
        label: "Ok",
        onClick: () => console.log("Ok"),
      },
    })
  }

  function onShowChat() {
    if (props.onShowChat) props.onShowChat()
  }


  return <>
    <div className="relative">
      <div className="flex gap-6 items-center py-2" id="menuroot">
        <div className="flex items-center gap-3">
          {(canUpdate("agents") || canCreate("agents")) && <Button variant="default" size="sm" className="rounded hover:opacity-90" onClick={() => onPlay()} title="Run Agent">
            <Play size={18} />
            Run
          </Button>}
          {(canUpdate("agents") || canCreate("agents")) && <Button variant="outline" size="sm" className="rounded hover:bg-sidebar-accent hover:text-white" onClick={() => onSave()} title="Save Agent">
            {saving && <LoaderCircle size={18} className="animate-spin" />}
            {!saving && <Save size={18} />}
            Save
          </Button>}
          {canCreate("agents") && app.agent.id && <Button variant="outline" size="sm" className="rounded hover:bg-sidebar-accent hover:text-white" onClick={() => onFork()} title="Fork Agent">
            {forking && <LoaderCircle size={18} className="animate-spin" />}
            {!forking && <GitFork size={18} />}
            Fork
          </Button>}
          {canUpdate("agents") && app.agent.fork_id && <Button variant="outline" size="sm" className="rounded hover:bg-sidebar-accent hover:text-white" onClick={async () => {
            const { data } = await supabase.from("agents").select("id, title").eq("id", Number(app.agent.fork_id)).single()
            setMergeTarget(data ? { id: data.id, title: data.title } : null)
            setMergeDialogOpen(true)
          }} title="Merge into original agent">
            {merging && <LoaderCircle size={18} className="animate-spin" />}
            {!merging && <GitMerge size={18} />}
            Merge
          </Button>}

          {(canUpdate("agents") || canCreate("agents")) && <Menubar className="bg-transparent! border-none! p-0 space-x-3 shadow-none h-4">
            <MenubarMenu>
              <MenubarTrigger asChild>
                <Button variant="outline" size="sm" className="rounded hover:bg-sidebar-accent hover:text-white" title="Agent Settings">
                  <Settings size={18} />
                </Button>
              </MenubarTrigger>
              <MenubarContent>
                <MenubarItem onClick={onSetAgent} title="Configure Agent Settings">
                  <div className="flex items-center gap-2">
                    <Settings size={16} />
                    Settings
                  </div>
                </MenubarItem>
                <MenubarItem onClick={onChangeDisplayData} title={app.agent.displayData ? "Hide Debug Data" : "Show Debug Data"}>
                  <div className="flex items-center gap-2">
                    <Bug size={16} />
                    {app.agent.displayData ? "Hide Debug Data" : "Show Debug Data"}
                  </div>
                </MenubarItem>
              </MenubarContent>
            </MenubarMenu>
          </Menubar>}
          {(app.agent.isConversational) && <Button variant="outline" size="sm" className="rounded hover:bg-sidebar-accent hover:text-white" onClick={onShowChat} title="Open Chat">
            <MessageCircle size={16} />
          </Button>}
        </div>
      </div>
    </div>

    <Modal form={agentForm} title="Agent Configuration" className="max-w-2xl">

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex w-full flex-wrap h-auto">
          <TabsTrigger className="grow" value="settings">Settings</TabsTrigger>
          <TabsTrigger className="grow" value="versions">Versions</TabsTrigger>
          <TabsTrigger className="grow" value="evaluation">Evaluation</TabsTrigger>
          <TabsTrigger className="grow" value="flags">Flags</TabsTrigger>
          <TabsTrigger className="grow" value="forms">Forms</TabsTrigger>
          <TabsTrigger className="grow" value="integrations">Integrations</TabsTrigger>
        </TabsList>
        <TabsContent value="settings" className="focus:ring-0 focus:ring-transparent focus:ring-offset-0">
          <div className="w-full flex flex-col gap-4 overflow-y-auto">
            <Row>
              <Input span={12} field={f.title} required />
            </Row>
            <Row>
              <InputTextArea rows={5} span={12} field={f.description} />
            </Row>
            <Row>
              <Select span={12} field={f.type} required />
            </Row>
            <Row>
              <div className="flex w-full col-span-12 items-end">
                <div className="flex-1">
                  <Input span={12} field={f.debuguid} />
                </div>
                <Button type="button" variant="outline" size="sm" className="ml-2 h-10 px-3 hover:bg-gray-50" onClick={onGenerateNewDebugUID} title="Generate new debug UID">
                  <RotateCcw className="h-4 w-4" />
                </Button>
              </div>
            </Row>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" className="rounded-sm w-full" title="Reset Agent State and History">Reset State and History</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete the Agent state.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={onResetState} >Continue</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </TabsContent>
        <TabsContent value="versions">
          <VersionSaver field={f.version} watch={watch} team={selectedTeam.id} rebuild={props.rebuild} hide={agentForm.modal.hide} />
        </TabsContent>
        <TabsContent value="evaluation">
          <EvaluationTab team={selectedTeam.id} selectedItems={evalSelectedItems} onSelectedItemsChange={setEvalSelectedItems} initialIds={app.agent.evalItems} initializedRef={evalInitialized} summaryLanguage={app.agent.summaryLanguage || ""} onSummaryLanguageChange={(v) => { app.agent.summaryLanguage = v || null }} />
        </TabsContent>
        <TabsContent value="flags">
          <EscalationFlagsTab key={settingsVersion} flags={escalationFlagsRef.current} flagsRef={escalationFlagsRef} />
        </TabsContent>
        <TabsContent value="forms">
          <FormsTab key={settingsVersion} formsRef={formsRef} team={selectedTeam.id} />
        </TabsContent>
        <TabsContent value="integrations">
          <IntegrationsTab />
        </TabsContent>
      </Tabs>


    </Modal>

    <AlertDialog open={mergeDialogOpen} onOpenChange={setMergeDialogOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Merge into original agent?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div>
              {mergeTarget && (
                <p className="mb-2 font-medium text-foreground">
                  Target: #{mergeTarget.id} — {mergeTarget.title}
                </p>
              )}
              <p>
                This will merge this fork's changes into the original agent. If the original was modified since this fork was created, non-conflicting changes from both sides will be preserved.
                A snapshot of the original will be saved to version history before merging.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onMerge}>
            {merging && <LoaderCircle size={16} className="animate-spin mr-2" />}
            Merge
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>


  </>


}
